import { useEffect, useState } from "react";
import type { HouseholdSettings, WeatherSummary } from "../../shared/models";
import { skyPhase, type SkyPhase } from "../../shared/sun";
import { activeSeasonalTheme, isSeasonalChoice } from "../../shared/themes";
import { useThemeLook } from "../themes";
import { useWeather } from "../data/queries";
import { useDevicePrefs } from "./devicePrefs";
import { Scenery, type Season, type SkyCondition } from "./Scenery";
import { useHousehold } from "./useHousehold";

/** What the sky is doing right now, from the observation (or this hour's forecast). */
export function skyCondition(w: WeatherSummary | undefined): SkyCondition {
  if (!w) return "partly";
  const icon = w.observation?.icon ?? w.hourly[0]?.icon ?? "unknown";
  const text = (w.observation?.description ?? w.hourly[0]?.shortForecast ?? "").toLowerCase();
  switch (icon) {
    case "thunder":
      return "storm";
    case "rain":
    case "showers":
      return "rain";
    case "snow":
    case "sleet":
      return "snow";
    case "fog":
    case "haze":
      return "fog";
    case "cloudy":
      return text.includes("overcast") ? "overcast" : "cloudy";
    case "partly-cloudy":
      return text.includes("mostly cloudy") ? "cloudy" : "partly";
    case "wind":
      return "partly";
    default:
      return "clear";
  }
}

export function seasonFor(date: Date, lat: number | null): Season {
  let m = date.getMonth(); // 0 = January
  if (lat !== null && lat < 0) m = (m + 6) % 12;
  if (m >= 2 && m <= 4) return "spring";
  if (m >= 5 && m <= 7) return "summer";
  if (m >= 8 && m <= 10) return "autumn";
  return "winter";
}

/** Moon phase from the mean synodic month (good to about a day — plenty for scenery). */
export function moonPhase(date: Date): { fraction: number; waxing: boolean } {
  const synodic = 29.530588853;
  const knownNew = Date.UTC(2000, 0, 6, 18, 14);
  const age = ((((date.getTime() - knownNew) / 86_400_000) % synodic) + synodic) % synodic;
  const angle = (age / synodic) * 2 * Math.PI;
  return { fraction: (1 - Math.cos(angle)) / 2, waxing: age < synodic / 2 };
}

/**
 * Owns the backdrop and the effective theme. Lives beside the dashboard (not
 * above it) so its periodic re-render never re-renders the widgets.
 */
export function Appearance() {
  const { settings, timeZone } = useHousehold();
  const { reduceEffects, density } = useDevicePrefs();
  const weather = useWeather(Boolean(settings.location));
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 5 * 60_000);
    return () => clearInterval(t);
  }, []);

  // Development only: preview any sky with ?sky=storm&season=winter&phase=night&theme=light&seasonal=halloween.
  const preview = import.meta.env.DEV ? new URLSearchParams(window.location.search) : null;
  const phase: SkyPhase = skyPhase(now, timeZone, settings.location);
  // "auto" follows the real sun: light by day, dark from dusk to dawn.
  const choice = (preview?.get("theme") as HouseholdSettings["theme"] | null) ?? settings.theme;
  const theme = choice === "auto" ? (phase === "day" ? "light" : "dark") : choice;
  const previewSeasonal = preview?.get("seasonal");
  const seasonal = activeSeasonalTheme(isSeasonalChoice(previewSeasonal) ? previewSeasonal : settings.seasonalTheme, now, timeZone);
  // The seasonal look switches on once its chunk is here, so colors and paintings change together.
  const look = useThemeLook(seasonal);
  const active = look ? seasonal : null;
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    if (active) root.dataset.seasonal = active;
    else delete root.dataset.seasonal;
    root.classList.toggle("reduce-effects", reduceEffects);
    root.dataset.density = density;
    const chrome = look?.chrome?.[theme] ?? (theme === "dark" ? "#0c0b0a" : "#e9e4dc");
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", chrome);
  }, [theme, active, look, reduceEffects, density]);

  const lat = settings.location?.lat ?? null;
  // The painting always follows the real sun, whatever the theme, so the wall matches the window.
  const scenePhase = (preview?.get("phase") as SkyPhase | null) ?? phase;
  const condition = (preview?.get("sky") as SkyCondition | null) ?? skyCondition(weather.data);
  const Accents = look?.Accents;
  return (
    <>
      <Scenery
        phase={scenePhase}
        condition={condition}
        season={(preview?.get("season") as Season | null) ?? seasonFor(now, lat)}
        moon={moonPhase(now)}
        animate={!reduceEffects}
        painting={look?.paintings?.[scenePhase]}
      />
      {Accents && <Accents phase={scenePhase} condition={condition} theme={theme} animate={!reduceEffects} />}
    </>
  );
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}
